import type { IBulkOperationRepository } from '@yawi/domain';
import type {
  BulkOperation,
  BulkOperationListParams,
  BulkOperationListResponse,
  BulkOperationOptions,
  BulkOperationResource,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiBulkOperationRepository implements IBulkOperationRepository {
  private readonly basePath = 'v1/admin/bulk-operations';

  async downloadTemplate(resource: BulkOperationResource): Promise<Blob> {
    const client = getApiClient();
    return client.get<Blob>(`${this.basePath}/templates/${resource}/download`, {
      responseType: 'blob',
    });
  }

  async importFile(
    file: File,
    resource: BulkOperationResource,
    tenantId: string,
    options?: BulkOperationOptions,
  ): Promise<BulkOperation> {
    const client = getApiClient();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('resource', resource);
    formData.append('tenantId', tenantId);
    if (options) {
      formData.append('options', JSON.stringify(options));
    }
    const response = await client.post<{ data: BulkOperation }>(`${this.basePath}/import`, formData);
    return (response as unknown as { data: BulkOperation }).data ?? (response as unknown as BulkOperation);
  }

  async getOperation(id: string): Promise<BulkOperation> {
    const client = getApiClient();
    const response = await client.get<{ data: BulkOperation }>(`${this.basePath}/${id}`);
    return (response as unknown as { data: BulkOperation }).data ?? (response as unknown as BulkOperation);
  }

  async listOperations(tenantId: string, params?: BulkOperationListParams): Promise<BulkOperationListResponse> {
    const client = getApiClient();
    const queryParams = new URLSearchParams({ tenantId });
    if (params?.type) queryParams.set('type', params.type);
    if (params?.resource) queryParams.set('resource', params.resource);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.limit != null) queryParams.set('limit', String(params.limit));
    if (params?.offset != null) queryParams.set('offset', String(params.offset));
    const url = `${this.basePath}?${queryParams.toString()}`;
    const response = await client.get<{ data: BulkOperation[]; total: number }>(url);
    const typed = response as unknown as { data: BulkOperation[]; total: number };
    if (typed.data && Array.isArray(typed.data)) {
      return { data: typed.data, total: typed.total ?? typed.data.length };
    }
    const arr = response as unknown as BulkOperation[];
    return { data: arr, total: arr.length };
  }

  async cancelOperation(id: string): Promise<BulkOperation> {
    const client = getApiClient();
    const response = await client.post<{ data: BulkOperation }>(`${this.basePath}/${id}/cancel`, {});
    return (response as unknown as { data: BulkOperation }).data ?? (response as unknown as BulkOperation);
  }
}
